import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from '@/components/ui/toaster.jsx';
import { ApiError } from '@/lib/api.js';
import TermPicker, { termMonthsOf, TERM_MAX_MONTHS } from '@/components/TermPicker.jsx';
import { fromApplicationLines } from '@/lib/quoteCompleteness.js';
import { createManualMarket, listCarrierOptions, quickLogQuote } from '@/services/markets.service.js';
import {
  MARKET_CHANNEL_OPTIONS, CHANNEL_PROMPT, CHANNEL_UNSELECTED,
  channelForCarrier, wholesalerForCarrier, needsWholesaler, wholesalerOptions, otherWholesalerOptions,
} from '@/lib/marketChannel.js';
import { marketsOnly } from '@/lib/carrierRoles.js';
import CrmContactPicker from '@/components/licenses/CrmContactPicker.jsx';
import { searchProducers } from '@/lib/producerSearch.js';

// THE PHANTOM ROW (prompt 93). Designed in prompt 67, held twice, built here.
//
// David: "I don't love the picker. Click + Log. Click the Carrier. Choose the
// How. So much to just get there. The UX blows." And the screen contradicted
// itself — existing rows had inline fields, adding one opened a modal, so the
// same surface ran two interaction models and the worse one was the one used
// most.
//
// A row that is always at the bottom of the ladder. Type the carrier, tab,
// commit; the row becomes real and a fresh phantom appears below it with the
// focus already in it. Five markets should be five names and a tab each.
//
// KEYBOARD THE WHOLE WAY, and that is the entire point of the feature: a broker
// logging six markets should never touch the mouse. Tab between fields, Enter
// commits from anywhere in the row, focus lands in the new phantom.

// THE MARKET LIST IS THE MARKETS. Prompt 94 item 1, amended by 97 item 1.
//
// Two different questions got confused here and the first answer was wrong.
//
// NOT ACCESS. This shipped filtering on lines_of_business and is_cobroker off
// "a market with no access must not appear at all". David: "a carrier i have no
// access to won't be in my list. confused. what?" HIS CARRIER LIST IS HIS
// MARKETS. There is no population of unreachable carriers to hide, and appetite
// (the red/yellow signal) never filters either: a carrier with a rule against
// this risk stays, says why, and is pickable.
//
// BUT ROLE, YES. "In the markets list, wholesalers shouldn't exist. A wholesaler
// is a response to 'how did you send it.'" And Markel is paper: policies get
// issued on it, submissions do not go to it. Neither is a market that is being
// hidden from him; neither is a market. The predicate lives in lib/carrierRoles
// so the long-form dialog reads the same one.
//
// The free-text escape stays, so nothing is unreachable regardless.
function submittableMarkets(carriers) {
  return marketsOnly(carriers);
}

// MATCH ON WHAT HE TYPES, NOT ON PREFIX ONLY. He types `accel`, not
// `Acceleration Aviation`. Substring over the name, the slug and the aliases,
// case- and punctuation-insensitive, so `usspecialty` finds "US Specialty
// Insurance Company" and `old republic` finds it with or without the space.
function normalise(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
}

function matchCarriers(list, query) {
  const q = normalise(query);
  if (!q) return list;
  const scored = [];
  for (const c of list) {
    const name = normalise(c.name);
    const slug = normalise(c.slug);
    const aliases = (c.aliases || []).map(normalise);
    const surfaces = [name, slug, ...aliases];
    const best = surfaces.reduce((acc, s) => {
      const i = s.indexOf(q);
      if (i < 0) return acc;
      // A match at the start beats one in the middle, so `star` puts Starr
      // above anything merely containing it.
      return acc == null ? i : Math.min(acc, i);
    }, null);
    if (best != null) scored.push({ c, best });
  }
  return scored
    .sort((a, b) => a.best - b.best || String(a.c.name).localeCompare(String(b.c.name)))
    .map((s) => s.c);
}

// The signal beside the name, never inside it. `!` and `~` used to be PREFIXED
// into the carrier's name, so a label carried two facts and neither could be
// read or filtered — carrier_slug holding market and paper, one component over.
// It also meant a broker typing `accel` had to know whether to type the
// punctuation, which is what would have made this autocomplete unusable.
function signalNote(c) {
  if (c.signal === 'red') return 'rule matched';
  if (c.signal === 'yellow') return 'declined before';
  return '';
}

const INPUT = 'rounded border border-border bg-bg px-2 py-1 text-[12px] text-text placeholder:text-text-dim focus:border-accent focus:outline-none';

function CarrierField({ carriers, value, onPick, onFreeText, onNext, inputRef, existingSlugs }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);

  const pool = useMemo(() => submittableMarkets(carriers), [carriers]);
  const hits = useMemo(() => matchCarriers(pool, query).slice(0, 8), [pool, query]);

  // The typed name is offered as itself when nothing matches it. David knows
  // markets TRACON does not; the picklist must not be a wall. It lands in
  // carrier_name_raw, flagged unlisted, for carrier admin to catch up later —
  // the same "configuration, not coding" rule as document_senders.
  const freeText = query.trim();
  const exact = hits.some((c) => normalise(c.name) === normalise(freeText) || normalise(c.slug) === normalise(freeText));
  const offerFreeText = freeText.length >= 2 && !exact;
  const rows = offerFreeText ? [...hits, { __free: true }] : hits;

  useEffect(() => { setCursor(0); }, [query]);

  // Show what was committed once a carrier is chosen; the field is the label.
  useEffect(() => {
    if (value) setQuery(value.name || value.raw || '');
  }, [value]);

  function choose(row) {
    if (!row) return;
    if (row.__free) {
      onFreeText(freeText);
      setQuery(freeText);
    } else {
      onPick(row);
      setQuery(row.name);
    }
    setOpen(false);
    onNext?.();
  }

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setCursor((c) => Math.min(c + 1, rows.length - 1)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); return; }
    if (e.key === 'Escape') { setOpen(false); return; }
    // Enter and Tab both TAKE the highlighted match. Tab that merely left the
    // field would drop what he typed and hand him an empty carrier two fields
    // later, which is the failure that makes an autocomplete feel wrong.
    if ((e.key === 'Enter' || e.key === 'Tab') && open && rows.length) {
      e.preventDefault();
      choose(rows[cursor] || rows[0]);
    }
  }

  return (
    <div className="relative min-w-0 flex-1">
      <input
        ref={inputRef}
        type="text"
        value={query}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); onPick(null); }}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onKeyDown={onKeyDown}
        placeholder="Type a market"
        aria-label="Market"
        className={`${INPUT} w-full`}
      />
      {open && rows.length ? (
        <ul className="absolute left-0 top-full z-30 mt-1 max-h-64 w-[280px] overflow-auto rounded border border-border bg-surface py-1 shadow-lg">
          {rows.map((row, i) => (
            <li key={row.__free ? '__free' : row.slug}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); choose(row); }}
                onMouseEnter={() => setCursor(i)}
                className={`flex w-full items-baseline justify-between gap-2 px-2.5 py-1 text-left text-[12px] ${i === cursor ? 'bg-accent-soft text-text' : 'text-text-muted'}`}
              >
                {row.__free ? (
                  <span>
                    Use &ldquo;{freeText}&rdquo;
                    <span className="ml-1.5 text-[10.5px] text-text-dim">not in your carrier list</span>
                  </span>
                ) : (
                  <>
                    <span className="truncate">{row.name}</span>
                    <span className="shrink-0 text-[10.5px] text-text-dim">
                      {existingSlugs.has(row.slug) ? 'on the ladder' : signalNote(row)}
                    </span>
                  </>
                )}
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

export default function PhantomMarketRow({ dealId, aircraft = [], markets = [], onLogged }) {
  const [carriers, setCarriers] = useState([]);
  const [picked, setPicked] = useState(null);       // a carriers row
  const [rawName, setRawName] = useState('');       // a market TRACON does not list
  const [channel, setChannel] = useState(CHANNEL_UNSELECTED);
  const [viaSlug, setViaSlug] = useState('');
  // WHO PRODUCED IT, asked beside the wholesaler because that is where it is
  // known (David 2026-09-03): the Old Republic submission is Jackie's, the IAT
  // one is his. Per market, carrier to carrier. { id, name } | null.
  const [producer, setProducer] = useState(null);
  const [clearing, setClearing] = useState(false);
  const [premium, setPremium] = useState('');
  const [term, setTerm] = useState('12');      // decision 14: 12 pre-picked
  const [irOpwOnly, setIrOpwOnly] = useState(null); // B14: null = untouched, nothing sent
  const premiumTyped = !clearing && Number(String(premium).replace(/[^\d.]/g, '')) > 0;
  const [saving, setSaving] = useState(false);
  // Bumping this remounts the row's fields, which is how "a fresh phantom
  // appears below it" is expressed without a second component.
  const [generation, setGeneration] = useState(0);
  const carrierRef = useRef(null);
  const channelRef = useRef(null);

  useEffect(() => {
    if (!dealId) return;
    listCarrierOptions(dealId)
      .then((d) => setCarriers(d.carriers || []))
      .catch(() => setCarriers([]));
  }, [dealId]);

  const existingSlugs = useMemo(
    () => new Set((markets || []).map((m) => m.carrier_slug).filter(Boolean)),
    [markets]
  );

  const wholesalers = useMemo(() => wholesalerOptions(carriers), [carriers]);
  const otherWholesalers = useMemo(
    () => otherWholesalerOptions(carriers, picked ? picked.slug : null),
    [carriers, picked]
  );

  function pickCarrier(c) {
    setPicked(c);
    if (!c) return;
    setRawName('');
    // The method PREFILLS from the carrier rather than being asked. One
    // derivation, shared with the long-form dialog (lib/marketChannel.js).
    const seeded = channelForCarrier(c);
    if (seeded) setChannel(seeded);
    const via = wholesalerForCarrier(c);
    if (via) setViaSlug(via);
  }

  function pickFreeText(name) {
    setPicked(null);
    setRawName(name);
    // Nothing is known about an unlisted market, so nothing is guessed. He
    // states the transport, which is the rule everywhere else on this door.
    setChannel(CHANNEL_UNSELECTED);
    setViaSlug('');
  }

  const named = !!picked || !!rawName.trim();
  const canCommit = !saving && named && !!channel && (!needsWholesaler(channel) || !!viaSlug);

  function reset() {
    setPicked(null); setRawName(''); setChannel(CHANNEL_UNSELECTED);
    setViaSlug(''); setProducer(null); setClearing(false); setPremium(''); setTerm('12'); setIrOpwOnly(null);
    setGeneration((g) => g + 1);
  }

  const commit = useCallback(async () => {
    if (!canCommit) {
      if (named && !channel) toast.error(CHANNEL_PROMPT);
      else if (needsWholesaler(channel) && !viaSlug) toast.error('Which wholesaler carried it?');
      else toast.error('Type a market first');
      return;
    }
    // The term is sent as he confirmed it; a bad one stops the row before the
    // market is written, not after (decision 14).
    if (premiumTyped && termMonthsOf(term) == null) {
      toast.error(`Pick the term: 12 months, 6 months, or Other with 1 to ${TERM_MAX_MONTHS} months.`);
      return;
    }
    setSaving(true);
    try {
      const res = await createManualMarket(dealId, {
        // Exactly one names the market. The database CHECK enforces it too.
        carrier_slug: picked ? picked.slug : null,
        carrier_name_raw: picked ? null : rawName.trim(),
        channel,
        submitted_to_slug: needsWholesaler(channel) ? viaSlug : null,
        producer_contact_id: needsWholesaler(channel) && producer?.id ? String(producer.id) : null,
        clearing,
      });
      const label = picked ? picked.name : rawName.trim();

      // A premium typed on the phantom row is a quote that already came back, so
      // it goes through the SAME quick-log door the sent rows use. Optional by
      // design: a `sent` row has no premium yet and must not demand one.
      const p = Number(String(premium).replace(/[^\d.]/g, ''));
      if (p > 0 && res?.market?.id) {
        const extras = { term_months: termMonthsOf(term), ...(typeof irOpwOnly === 'boolean' ? { ir_opw_only: irOpwOnly } : {}) };
        const body = (aircraft || []).length === 1
          ? { aircraft_premiums: [{ tail_number_id: aircraft[0].tail_number_id, premium: p }], ...extras }
          : { premium: p, ...extras };
        try {
          const logged = await quickLogQuote(dealId, res.market.id, body);
          // B6: what the log took from the application, in the server's list.
          const seeded = fromApplicationLines(logged?.from_application, null, aircraft);
          toast.success(`${label} logged at $${p.toLocaleString()}`,
            seeded.length ? { description: `From the application: ${seeded.join(', ')}` } : undefined);
        } catch (qErr) {
          const b = qErr instanceof ApiError ? qErr.body : null;
          toast.error(`${label} logged, but the premium did not: ${b?.error || qErr.message}`);
        }
      } else {
        toast.success(clearing ? `${label} cleared` : `${label} logged`);
      }
      reset();
      onLogged?.();
      // The focus lands in the new phantom. Without this the keyboard run ends
      // after one market and the whole point of the row is gone.
      window.setTimeout(() => carrierRef.current?.focus(), 0);
    } catch (err) {
      const body = err instanceof ApiError ? err.body : null;
      toast.error(body?.error || err.message || 'Could not log it');
    } finally {
      setSaving(false);
    }
  }, [canCommit, named, channel, viaSlug, producer, dealId, picked, rawName, clearing, premium, aircraft, onLogged, term, irOpwOnly]);

  // Enter commits from ANY field in the row. Not just the last one: he finishes
  // typing a carrier whose method prefilled and there is nothing else to say.
  function onRowKeyDown(e) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      commit();
    }
  }

  return (
    <div
      onKeyDown={onRowKeyDown}
      className="flex flex-wrap items-center gap-x-2 gap-y-1.5 border-t border-dashed border-border/70 py-2.5"
    >
      <CarrierField
        key={generation}
        inputRef={carrierRef}
        carriers={carriers}
        value={picked ? { name: picked.name } : (rawName ? { raw: rawName } : null)}
        onPick={pickCarrier}
        onFreeText={pickFreeText}
        onNext={() => window.setTimeout(() => channelRef.current?.focus(), 0)}
        existingSlugs={existingSlugs}
      />

      {/* THE HOW COMES AFTER, NOT BEFORE (prompt 104 C2). David: "the 'type a
          market' just doesn't feel right. The 'how' could literally be a small
          dropdown after the line is added."
          Almost always it IS after, because picking a carrier prefills the
          channel from its routing_type — 21 of 23 active carriers have one. So
          this control only appears when the carrier does NOT imply an answer,
          which is the minority case and the only one where the transport is
          genuinely unstated. It is not removed: recordSend refuses a submission
          whose transport nobody stated, and reintroducing an unstated one is
          the Jennings defect. */}
      {!channel ? (
      <select
        ref={channelRef}
        value={channel}
        onChange={(e) => setChannel(e.target.value)}
        aria-label={CHANNEL_PROMPT}
        className={`${INPUT} w-[150px]`}
      >
        {/* The unselected sentinel is the QUESTION, not an answer. It cannot be
            committed and the server 400s on it independently. */}
        <option value={CHANNEL_UNSELECTED}>{CHANNEL_PROMPT}</option>
        {MARKET_CHANNEL_OPTIONS.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      ) : (
        // Prefilled: shown as a quiet fact with one click to change it, rather
        // than a control he has to visit on every row.
        <button
          type="button"
          onClick={() => setChannel(CHANNEL_UNSELECTED)}
          title="Change how it went"
          className="shrink-0 rounded border border-border px-2 py-1 text-[11px] text-text-muted hover:border-border-strong hover:text-text"
        >
          {(MARKET_CHANNEL_OPTIONS.find((o) => o.value === channel) || {}).label || channel}
        </button>
      )}

      {needsWholesaler(channel) ? (
        <select
          value={viaSlug}
          onChange={(e) => setViaSlug(e.target.value)}
          aria-label="Which wholesaler carried it"
          className={`${INPUT} w-[150px]`}
        >
          {/* The carrier above writes the paper. This is who carried it there.
              The cobrokers lead because they are who he routes through; the rest
              are still reachable, because a flag being right today is not a
              reason to make everything else unpickable (prompt 94 item 1). */}
          <option value="">Which wholesaler?</option>
          <optgroup label="Wholesalers">
            {wholesalers.map((c) => (
              <option key={c.slug} value={c.slug}>{c.name || c.slug}</option>
            ))}
          </optgroup>
          <optgroup label="Everyone else">
            {otherWholesalers.map((c) => (
              <option key={c.slug} value={c.slug}>{c.name || c.slug}</option>
            ))}
          </optgroup>
        </select>
      ) : null}

      {/* The producer at the wholesaler, if one produced it. Sets the brand on
          the paperwork this market's quote will print (IBI for an IBI producer).
          Optional: blank means NDEMNIFLY. */}
      {needsWholesaler(channel) ? (
        <div className="w-[190px] shrink-0" title="Who produced it, at the wholesaler. Blank means NDEMNIFLY.">
          <CrmContactPicker value={producer} searchFn={searchProducers} placeholder="Producer (optional)" onSelect={setProducer} />
        </div>
      ) : null}

      {/* A CLEARANCE IS NOT A SUBMISSION, and it is what David does most at 90
          days out. Nothing was asked of the market; the right to quote was
          reserved. The row reads "cleared, not submitted" and the follow-up
          clock leaves it alone. */}
      {/* IT IS AN INPUT AND IT READ LIKE A STATUS (prompt 104 C3). David: "I'm
          not sure what the clearing checkbox does...? Did I tell it it's
          awaiting referral, or is it telling me?" A bare word that could be
          either voice is worse than a longer one that cannot, so it says the
          ACT — the same wording as the row-menu item that does the same thing. */}
      <label className="flex shrink-0 cursor-pointer items-center gap-1 text-[11px] text-text-muted" title="Nothing was submitted: the right to quote was reserved. The follow-up clock will not chase it.">
        <input type="checkbox" checked={clearing} onChange={(e) => setClearing(e.target.checked)} />
        a clearance, not a submission
      </label>

      <span className="flex shrink-0 items-center gap-1">
        <span className="text-[12px] text-text-muted">$</span>
        <input
          type="text"
          inputMode="decimal"
          value={premium}
          onChange={(e) => setPremium(e.target.value)}
          disabled={clearing}
          placeholder={clearing ? 'n/a' : 'premium'}
          aria-label="Premium, optional"
          title={clearing ? 'A clearance has no premium: nothing was asked of them.' : 'Optional. A sent row has no premium yet.'}
          className={`${INPUT} w-[92px] font-semibold text-success placeholder:font-normal disabled:opacity-40`}
        />
      </span>

      {/* A PREMIUM IS A QUOTE, SO IT GETS ITS TERM (decision 14: 12 pre-picked,
          sent as shown) and the "I&R, OPW only" box (B14: untouched sends
          nothing). Only once a premium is typed: a sent row asks neither. */}
      {premiumTyped ? (
        <span className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1">
          <TermPicker value={term} onChange={setTerm} disabled={saving} />
          <label className="flex items-center gap-1.5 text-[11px] text-text-muted">
            <input type="checkbox" checked={irOpwOnly === true} disabled={saving} data-ir-opw-only
              onChange={(e) => setIrOpwOnly(e.target.checked)} />
            I&amp;R, OPW only
          </label>
        </span>
      ) : null}

      <button
        type="button"
        onClick={commit}
        disabled={!canCommit}
        className="shrink-0 rounded-[5px] border border-border px-2.5 py-1 text-[12px] hover:border-border-strong hover:bg-surface-strong disabled:opacity-40"
      >
        {saving ? '…' : 'Add'}
      </button>

      {/* The date is not asked. It defaults to today, which is what the sent
          history entry records, and asking would put a field in front of him
          that is right every time he does not touch it. */}
      <span className="w-full text-[10.5px] text-text-dim">
        {rawName && !picked
          ? `"${rawName.trim()}" is not in your carrier list. It will log as an unlisted market.`
          : 'Enter logs it and starts the next one. Sent today.'}
      </span>
    </div>
  );
}
